import {
  Description,
  DoneAll,
  Download,
  Image as ImageIcon,
  Person,
} from "@mui/icons-material";
import { Avatar, Box, Link, Skeleton, Typography } from "@mui/material";
import { format, isToday } from "date-fns";
import { ConversationDto } from "libs/cellera-sdk";
import { useMemo, useState } from "react";
import { useCelleraTheme } from "theme/useCelleraTheme";
import { Patient } from "types";
import { Attachment, ChatMessage, SenderType } from "types/socket";
import { formatPatientName } from "utils/patient";

import ImagePreviewModal from "./ImagePreviewModal";

type MessageProps = {
  message: ChatMessage;
  conversation: ConversationDto;
};

const Message: React.FC<MessageProps> = ({
  message,
  conversation,
}: MessageProps) => {
  const { content, senderType, createdAt, attachments, readAt } = message;

  const { colors } = useCelleraTheme();
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [loadingImages, setLoadingImages] = useState<Record<string, boolean>>(
    {}
  );

  const sendAt = useMemo(() => {
    return isToday(createdAt)
      ? format(createdAt, "hh:mm a")
      : format(createdAt, "MM/dd/yyyy hh:mm a");
  }, [createdAt]);

  const isPatient = useMemo(() => {
    const type = senderType;
    return type === SenderType.PATIENT;
  }, [senderType]);

  const senderName = useMemo(() => {
    if (isPatient) {
      return formatPatientName(conversation.patient as Patient);
    }
    return conversation.provider.practiceName;
  }, [isPatient, conversation.provider.practiceName, conversation.patient]);

  const senderPhotoUrl = useMemo(() => {
    if (isPatient) {
      return conversation.patient?.photoUrl;
    }
    return conversation.provider.photoUrl;
  }, [
    isPatient,
    conversation.patient?.photoUrl,
    conversation.provider.photoUrl,
  ]);

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const renderImageAttachment = (attachment: Attachment) => {
    const isLoading = loadingImages[attachment.url] !== false;

    return (
      <Box
        key={attachment.url}
        sx={{
          borderRadius: "8px",
          overflow: "hidden",
          width: "424px",
          position: "relative",
        }}
      >
        {isLoading && (
          <Skeleton
            variant="rectangular"
            width="100%"
            height={424}
            sx={{
              borderRadius: "8px",
            }}
          />
        )}
        <Box
          component="img"
          src={attachment.url}
          alt={attachment.filename}
          sx={{
            width: "100%",
            height: 424,
            objectFit: "cover",
            display: isLoading ? "none" : "block",
            cursor: "pointer",
            "&:hover": {
              opacity: 0.9,
            },
          }}
          onLoad={() => {
            setLoadingImages((prev) => ({
              ...prev,
              [attachment.url]: false,
            }));
          }}
          onClick={() => setPreviewImage(attachment.url)}
        />
      </Box>
    );
  };

  const renderFileAttachment = (attachment: Attachment) => {
    const isPdf = attachment.mimeType === "application/pdf";
    return (
      <Box
        key={attachment.url}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          p: 1.5,
          borderRadius: "8px",
          border: `1px solid ${colors.border.light}`,
          backgroundColor: colors.background.grayLight,
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: colors.background.paper,
            borderRadius: "4px",
          }}
        >
          {isPdf ? (
            <Description sx={{ color: colors.text.secondary }} />
          ) : (
            <ImageIcon sx={{ color: colors.text.secondary }} />
          )}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Link
            href={attachment.url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              fontSize: "14px",
              fontWeight: 500,
              color: colors.text.primary,
              textDecoration: "none",
              display: "block",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
          >
            {attachment.filename}
          </Link>
          <Typography
            variant="caption"
            sx={{
              fontSize: "12px",
              color: colors.text.secondary,
            }}
          >
            {formatFileSize(attachment.size)}
          </Typography>
        </Box>
        <Box
          component="a"
          href={attachment.url}
          target="_blank"
          download={attachment.filename}
          rel="noopener noreferrer"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 32,
            height: 32,
            borderRadius: "4px",
            backgroundColor: colors.background.paper,
            cursor: "pointer",
            transition: "background-color 0.2s",
            "&:hover": {
              backgroundColor: colors.background.grayLight,
            },
          }}
        >
          <Download
            sx={{
              color: colors.text.secondary,
              fontSize: "20px",
            }}
          />
        </Box>
      </Box>
    );
  };

  const renderAttachmentsByMimeType = () => {
    if (!attachments || attachments.length === 0) {
      return null;
    }

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        {attachments.map((attachment) =>
          attachment.mimeType.startsWith("image/")
            ? renderImageAttachment(attachment)
            : renderFileAttachment(attachment)
        )}
      </Box>
    );
  };

  const renderTextContent = () => {
    if (!content) {
      return null;
    }

    return (
      <Typography
        variant="body2"
        sx={{
          fontSize: "14px",
          color: colors.text.primary,
          overflowWrap: "break-word",
          wordBreak: "break-word",
          whiteSpace: "pre-wrap",
          wordWrap: "break-word",
        }}
      >
        {content}
      </Typography>
    );
  };

  const renderContent = () => {
    const attachmentContent = renderAttachmentsByMimeType();
    const textContent = renderTextContent();

    if (!attachmentContent) {
      return textContent;
    }

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        {attachmentContent}
        {textContent}
      </Box>
    );
  };

  return (
    <>
      <Box
        sx={{
          padding: 2,
          border: `1px solid ${colors.border.light}`,
          borderRadius: "16px",
          borderBottomLeftRadius: isPatient ? "0px" : "16px",
          borderBottomRightRadius: !isPatient ? "0px" : "16px",
          backgroundColor: isPatient
            ? colors.background.paper
            : colors.background.primaryLight,
          display: "flex",
          flexDirection: "column",
          gap: 1,
          width: "fit-content",
          maxWidth: "100%",
          alignSelf: isPatient ? "flex-start" : "flex-end",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Avatar
            sx={{
              bgcolor: colors.secondary.main,
            }}
            src={senderPhotoUrl}
          >
            <Person sx={{ color: colors.text.inverse }} />
          </Avatar>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 500,
              fontSize: "14px",
              color: colors.text.primary,
            }}
          >
            {senderName}
          </Typography>
        </Box>
        <Box>
          {renderContent()}
          <Box display="flex" alignItems="center" gap={1}>
            <Typography
              variant="caption"
              sx={{
                fontSize: "12px",
                color: colors.text.secondary,
                mt: 0.5,
                display: "block",
              }}
            >
              {sendAt}
            </Typography>

            {readAt && !isPatient && (
              <DoneAll
                sx={{ color: colors.text.secondary, fontSize: "16px" }}
              />
            )}
          </Box>
        </Box>
      </Box>
      {previewImage && (
        <ImagePreviewModal
          open={!!previewImage}
          onClose={() => setPreviewImage(null)}
          imageUrl={previewImage}
        />
      )}
    </>
  );
};

export default Message;
